import { useMemo, useRef, useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { workforce } from '@cwos/api-client';
import type { LocationVerification } from '@cwos/ui-shared';
import {
  DualTimestamp,
  FilterSummary,
  Pill,
  Refused,
  SkeletonSet,
  VerificationBadge,
  WorkforceMap,
  formatMeters,
  formatMinutes,
  gridCluster,
  type GeoMarker,
  type GeoRoute,
} from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { mayReadSessionTrail, SESSION_TRAIL_KEY, usePermissions } from '../permissions.js';
import { listPhase } from '../listState.js';
import { pickerState } from '../pickerState.js';
import { ListState } from '../ListStates.js';
import {
  addDays,
  browserTimezone,
  distinctPrograms,
  distinctRoles,
  geofenceTone,
  matchesFilters,
  sessionTone,
  sessionTrailState,
  shouldReadSessionTrail,
  toLocalDateString,
  visibleFences,
  visibleSites,
  type StatusFilter,
  type WorkforceFilters,
} from './workforceLogic.js';
import { formatShiftWindow } from '../../lib/timeFormat.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Workforce map & location history (Track A; doc "Daily operations map").
 * A supervisor reads the day's workforce geographically without opening each
 * record: work sites as circles, workers as (clustered) markers, and — on
 * selecting a session — the worker's actual breadcrumb POLYLINE with a
 * chronological timeline. Renders on the interactive Google Maps JS layer when
 * a referrer-restricted client key (`VITE_GOOGLE_MAPS_API_KEY`) is present,
 * falling back to the dependency-free SVG map otherwise. Every read is audited;
 * this screen never fabricates a permission.
 *
 * TWO PERMISSIONS, NOT ONE. The map (`GET /location/workforce/:date`) is gated
 * on `location_history:view`, and so is the route into this screen. The
 * TIMELINE (`GET /location/sessions/:id/timeline`) is gated on that key AND on
 * `breadcrumb:view`, because it serves the raw breadcrumb polyline — the same
 * array `/location/sessions/:id/breadcrumbs` serves, at the same price. Both
 * keys are direct grants held by no role, so a holder of the map key alone is
 * the ordinary case rather than an exotic one, and this screen used to offer
 * them a "View route history" button that could only ever 403.
 */

const DEFAULT_REASON = 'Reviewing workforce locations';

function toInputDate(d: Date): string {
  return toLocalDateString(d);
}

export function Workforce() {
  const { me } = useAuth();
  const permissions = usePermissions();
  /**
   * Whether the trail half of this screen is reachable at all.
   *
   * Not `has('location_history:view')`: that is the key that got the reader
   * through the door, and testing it here answers "am I on this screen" rather
   * than "may I read a route". `mayReadSessionTrail` is the one client-side
   * statement of what the timeline route actually requires, so it cannot drift
   * from the server the way six separate `has(...)` calls could.
   *
   * And it is the SCOPE-AWARE question, because the timeline route names the
   * scope itself rather than inheriting it: `guard(..., 'location_history:view',
   * ORG_WIDE_LOCATION_HISTORY)` with `ORG_WIDE_LOCATION_HISTORY = { scopeType:
   * 'org' }`, above the comment "a PROGRAM-scoped `location_history:view` row
   * does NOT open these routes; it is refused outright rather than silently
   * promoted to org-wide". `requireTrailDoor` then asks `can(principal.authz,
   * 'breadcrumb:view')` at the same default. Read off the flattened key list,
   * a program-scoped holder of either key was offered "View route history" for
   * a route that had already decided, by scope, to refuse them — the exact
   * button this predicate was written to withdraw.
   *
   * Unknown-vs-unheld does not need a third branch here, unlike `readScope`:
   * `<RequirePermission permission="location_history:view">` wraps both routes
   * into this screen, so a set that is still loading renders a skeleton and a
   * set that could not be read renders a connection error, both ABOVE this
   * component. Anything that reaches here has a resolved set, and `has` is
   * answering from it.
   */
  const canReadTrail = mayReadSessionTrail(permissions);
  const tz = useMemo(() => browserTimezone(), []);
  // Deep-linkable. Every other screen that knows about a worker — Time clock,
  // the worker profile — can now hand somebody straight to that person's day
  // instead of leaving them to find the map and re-pick the date and the name.
  // Without this the location experience could only be entered from the top,
  // which is most of why it could not be found at all.
  const search = useSearch({ strict: false }) as {
    date?: string;
    worker?: string;
    session?: string;
  };
  const [date, setDate] = useState(() => search.date ?? toInputDate(new Date()));
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [program, setProgram] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [onMapOnly, setOnMapOnly] = useState(false);
  const [site, setSite] = useState('');
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(search.worker ?? null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(search.session ?? null);
  // A link that names a session is a request to SEE that session, so the
  // timeline is already asked for. Arriving without one still requires the
  // explicit click, because a timeline read is audited.
  const [timelineRequested, setTimelineRequested] = useState(search.session != null);
  const [reason, setReason] = useState(DEFAULT_REASON);

  // The map fetches once per (date, tz); the reason is an audit annotation, not
  // part of cache identity, so it is read through a ref to avoid refetching the
  // map on every keystroke.
  const reasonRef = useRef(reason);
  reasonRef.current = reason;

  const map = useQuery({
    queryKey: ['workforce', date, tz],
    queryFn: () =>
      workforce.workforceMap(date, { tz, reason: reasonRef.current.trim() || undefined }),
  });

  const timeline = useQuery({
    queryKey: ['workforce', 'timeline', selectedSessionId],
    queryFn: () =>
      workforce.sessionTimeline(selectedSessionId!, {
        reason: reason.trim() === '' ? undefined : reason.trim(),
      }),
    // `canReadTrail` is part of `enabled`, not only of what is rendered. A deep
    // link carrying `?session=` sets `timelineRequested` before anybody clicks
    // anything, so without it the screen fires a read it already knows will be
    // refused — a 403 in the network log, a cached rejection under this query
    // key, and a reason-for-access header sent for a reveal nobody asked for.
    enabled: shouldReadSessionTrail(selectedSessionId, timelineRequested, canReadTrail),
  });

  const filters: WorkforceFilters = { query, role, program, status, onMapOnly };
  const workers = map.data?.workers ?? [];
  const filtered = useMemo(
    () => workers.filter((w) => matchesFilters(w, filters, hidden)),
    [workers, query, role, program, status, onMapOnly, hidden],
  );
  const roles = useMemo(() => distinctRoles(workers), [workers]);
  const programs = useMemo(() => distinctPrograms(workers), [workers]);

  const selectedWorker = workers.find((w) => w.workerId === selectedWorkerId) ?? null;

  // Map markers: cluster nearby workers; singletons stay individually selectable.
  const markers = useMemo<GeoMarker[]>(() => {
    const located = filtered
      .filter((w) => w.lastLocation != null)
      .map((w) => ({
        worker: w,
        latitude: w.lastLocation!.latitude,
        longitude: w.lastLocation!.longitude,
      }));
    const clusters = gridCluster(located, 150);
    return clusters.map((c) => {
      if (c.count === 1) {
        const w = c.items[0]!.worker;
        return {
          id: w.workerId,
          latitude: c.latitude,
          longitude: c.longitude,
          kind: 'worker' as const,
          tone: geofenceTone(w.lastLocation!.geofenceStatus),
          label: w.displayName,
          selected: w.workerId === selectedWorkerId,
        };
      }
      return {
        id: `cluster:${c.items
          .map((i) => i.worker.workerId)
          .sort()
          .join('|')}`,
        latitude: c.latitude,
        longitude: c.longitude,
        kind: 'cluster' as const,
        count: c.count,
      };
    });
  }, [filtered, selectedWorkerId]);

  // Selected session route + its event markers.
  const routes = useMemo<GeoRoute[]>(() => {
    if (!timeline.data) return [];
    return [
      {
        id: timeline.data.sessionId,
        points: timeline.data.breadcrumbs.map((b) => ({
          latitude: b.latitude,
          longitude: b.longitude,
        })),
      },
    ];
  }, [timeline.data]);

  const routeMarkers = useMemo<GeoMarker[]>(() => {
    if (!timeline.data) return [];
    const out: GeoMarker[] = [];
    const byKind = (kind: 'start' | 'end') => {
      const p = timeline.data!.punches.find(
        (x) => x.kind === (kind === 'start' ? 'clock_in' : 'clock_out'),
      );
      return p?.latitude != null && p?.longitude != null
        ? { latitude: p.latitude, longitude: p.longitude }
        : null;
    };
    const start = byKind('start');
    const end = byKind('end');
    if (start) out.push({ id: `${timeline.data.sessionId}:start`, ...start, kind: 'start' });
    if (end) out.push({ id: `${timeline.data.sessionId}:end`, ...end, kind: 'end' });
    for (const s of timeline.data.route.stops) {
      out.push({
        id: `${timeline.data.sessionId}:stop:${s.startedAt}`,
        latitude: s.centroid.latitude,
        longitude: s.centroid.longitude,
        kind: 'stop',
      });
    }
    return out;
  }, [timeline.data]);

  // The Site layer: real Sites from the Site domain, drawn as squares so a place
  // is never mistaken for a person. Sites with no coordinates simply have no pin.
  const allSites = map.data?.sites ?? [];
  const shownSites = useMemo(() => visibleSites(allSites, site), [allSites, site]);
  const siteMarkers = useMemo<GeoMarker[]>(
    () =>
      shownSites.map((s) => ({
        id: `site:${s.siteId}`,
        latitude: s.latitude,
        longitude: s.longitude,
        kind: 'site' as const,
        label: s.name,
      })),
    [shownSites],
  );
  const shownFences = useMemo(
    () => visibleFences(map.data?.geofences ?? [], site),
    [map.data?.geofences, site],
  );

  const allMarkers = useMemo(
    () => [...siteMarkers, ...markers, ...routeMarkers],
    [siteMarkers, markers, routeMarkers],
  );

  function selectWorker(id: string) {
    setSelectedWorkerId(id);
    setSelectedSessionId(null);
    setTimelineRequested(false);
  }

  function selectSession(id: string) {
    setSelectedSessionId(id);
    setTimelineRequested(false);
  }

  function toggleHidden(id: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const narrowings = [
    query.trim() !== '' ? { label: `name “${query.trim()}”` } : null,
    role !== '' ? { label: `role ${role}` } : null,
    program !== '' ? { label: `program ${program}` } : null,
    status !== 'all' ? { label: `status ${status}` } : null,
    onMapOnly ? { label: 'on shift now' } : null,
    // Hiding people removed them from `filtered` AND from the map markers, and
    // was never declared — so with no other filter set the summary printed
    // "That's everything — no hidden filters" over a list with people
    // deliberately hidden from it.
    hidden.size > 0 ? { label: `${hidden.size} hidden by you` } : null,
  ].filter((n): n is { label: string } => n != null);

  /**
   * The map read decides everything on this screen, including the roster rail
   * beside it — and the rail was outside every branch. During loading and after
   * a failure or a 403 it went on rendering "0 shown" and "No workers match."
   * next to the skeleton or the error panel: a count and an affirmative empty
   * sentence about a day nobody had been told anything about.
   *
   * `location_history:view` is granted to no role by default, so a refusal is
   * the COMMON case here, not an edge one.
   */
  const railPhase = listPhase(map, map.data === undefined ? undefined : filtered.length);
  /**
   * The same read behind the filter dropdowns. `distinctRoles`/`distinctPrograms`
   * run over `map.data?.workers ?? []`, so an unanswered map produced three
   * dropdowns each claiming the day had none of that thing.
   */
  const filterPicker = pickerState(
    listPhase(map, map.data === undefined ? undefined : workers.length),
    'people on the map',
    '',
  );

  return (
    <div className="rn-col">
      <h1 className="rn-card-title" style={{ marginBottom: 'var(--space-12)' }}>
        Workforce map
      </h1>
      <FilterSummary
        scope={{
          what: 'workers with a recorded shift',
          scope: me ? (me.orgName ?? me.orgId) : 'your organization',
          period: date,
          narrowings,
        }}
      />

      {/* Date + controls */}
      <section className="rn-card">
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-12)',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button type="button" className="rn-link" onClick={() => setDate((d) => addDays(d, -1))}>
            ← Prev
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value !== '' && setDate(e.target.value)}
            aria-label="Selected date"
          />
          <button type="button" className="rn-link" onClick={() => setDate((d) => addDays(d, 1))}>
            Next →
          </button>
          <span
            style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap' }}
          >
            <input
              placeholder="Search staff"
              aria-label="Search staff"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ minWidth: 160 }}
            />
            {/* The filter options are derived from the map payload, so on a
                failure or a refusal these dropdowns claim the day had no roles,
                no programs and no sites. `filterPicker` puts the reason in the
                placeholder rather than leaving an empty, operable-looking
                control. */}
            <select
              aria-label="Role filter"
              value={role}
              disabled={filterPicker.disabled}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="">
                {filterPicker.disabled ? filterPicker.placeholder : 'All roles'}
              </option>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <select
              aria-label="Program filter"
              value={program}
              disabled={filterPicker.disabled}
              onChange={(e) => setProgram(e.target.value)}
            >
              <option value="">
                {filterPicker.disabled ? filterPicker.placeholder : 'All programs'}
              </option>
              {programs.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <select
              aria-label="Site filter"
              value={site}
              disabled={filterPicker.disabled}
              onChange={(e) => setSite(e.target.value)}
            >
              <option value="">
                {filterPicker.disabled ? filterPicker.placeholder : 'All sites'}
              </option>
              {allSites.map((s) => (
                <option key={s.siteId} value={s.siteId}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Status filter"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="terminated">Terminated</option>
            </select>
            <label style={{ display: 'inline-flex', gap: 'var(--space-6)', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={onMapOnly}
                onChange={(e) => setOnMapOnly(e.target.checked)}
              />
              On shift now
            </label>
            <button
              type="button"
              className="rn-link"
              onClick={() => {
                setQuery('');
                setRole('');
                setProgram('');
                setSite('');
                setStatus('all');
                setOnMapOnly(false);
                setHidden(new Set());
              }}
            >
              Reset
            </button>
          </span>
        </div>

        {/* Screen-level reason-for-access: audited with BOTH the map and the
            session-timeline reads (location_history:view is a default-deny,
            audited reveal). */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-12)',
            alignItems: 'center',
            marginTop: 'var(--space-12)',
            paddingTop: 'var(--space-12)',
            borderTop: '1px solid var(--color-line)',
          }}
        >
          <label style={{ display: 'grid', gap: 'var(--space-4)', flex: 1 }}>
            <span className="rn-muted">Reason for access (recorded in the audit log)</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              aria-label="Reason for access"
            />
          </label>
        </div>
      </section>

      {/* Map + list */}
      <div className="rn-grid" style={{ gridTemplateColumns: '1fr 340px' }}>
        <div>
          {map.isLoading ? (
            <SkeletonSet surface="table" rows={4} />
          ) : map.isError && !map.data ? (
            <LoadFailure
              error={map.error}
              message="We couldn’t load the workforce map just now."
              onRetry={() => void map.refetch()}
            />
          ) : (
            <WorkforceMap
              fences={shownFences}
              markers={allMarkers}
              routes={routes}
              selectedMarkerId={selectedWorkerId ?? undefined}
              onMarkerSelect={selectWorker}
              mapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
              emptyMessage="No workers with a recorded shift on this date."
            />
          )}
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
            Squares are your sites; circles are people. Worker markers show each person’s last known
            position, and overlapping workers are clustered. Raw routes are revealed only when you
            open a session below — and that view is recorded in the audit log.
          </p>
        </div>

        <section className="rn-card" style={{ alignSelf: 'start' }}>
          <div className="rn-card-head">
            <h2 className="rn-card-title">Workers</h2>
            <span className="rn-muted">
              {map.data === undefined ? 'not read yet' : `${filtered.length} shown`}
            </span>
          </div>
          <ListState
            phase={railPhase}
            surface="table"
            rows={4}
            failedMessage="The workforce map didn’t load, so this list is empty for that reason — not because nobody worked today."
            onRetry={() => void map.refetch()}
            refused={
              <Refused
                heading="Positions are not shown to you"
                message="Viewing where people are needs the location-history permission, which is granted to no role by default. This is not a day with nobody working."
              />
            }
            empty={<p className="rn-muted">No workers match.</p>}
          >
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'grid',
                gap: 'var(--space-8)',
              }}
            >
              {/*
                The roster row used to be a bare `<li onClick>`: no tabIndex, no
                key handler, no role. Selecting a worker is the primary action on
                this screen and it could only be done with a mouse — and because
                selection showed up as a background tint and nothing else, a
                screen reader was never told which row was current either.

                The row already contains a Show/Hide button, so the row itself
                cannot become a button (nested buttons are invalid HTML). The
                selectable region is a real `<button>` instead: Enter and Space,
                the app-wide `:focus-visible` ring, and `aria-current` all come
                with it, and the Show/Hide button stays a sibling rather than a
                child, so no `stopPropagation` is needed to keep one keystroke
                from firing two actions.
              */}
              {filtered.map((w) => (
                <li
                  key={w.workerId}
                  data-selected={w.workerId === selectedWorkerId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-8)',
                    padding: 'var(--space-8)',
                    borderRadius: 'var(--radius-sm)',
                    background:
                      w.workerId === selectedWorkerId ? 'var(--color-sky-soft)' : 'transparent',
                  }}
                >
                  <button
                    type="button"
                    className="wf-roster-pick"
                    aria-current={w.workerId === selectedWorkerId ? 'true' : undefined}
                    onClick={() => selectWorker(w.workerId)}
                  >
                    <span style={{ fontWeight: 600 }}>{w.displayName}</span>
                    <span className="rn-muted" style={{ marginLeft: 'auto' }}>
                      {w.roleTitle ?? '—'}
                    </span>
                    {w.hasOpenSession ? (
                      <Pill variant="good" dot>
                        on shift
                      </Pill>
                    ) : (
                      <Pill variant="plain">{w.employmentStatus}</Pill>
                    )}
                  </button>
                  <button
                    type="button"
                    className="rn-link"
                    aria-label={hidden.has(w.workerId) ? 'Show worker' : 'Hide worker'}
                    onClick={() => toggleHidden(w.workerId)}
                  >
                    {hidden.has(w.workerId) ? 'Show' : 'Hide'}
                  </button>
                </li>
              ))}
            </ul>
          </ListState>
        </section>
      </div>

      {/* Selected worker → sessions → route history */}
      {selectedWorker ? (
        <WorkerDetail
          worker={selectedWorker}
          selectedSessionId={selectedSessionId}
          onSelectSession={selectSession}
          onViewRoute={() => setTimelineRequested(true)}
          timelineRequested={timelineRequested}
          canReadTrail={canReadTrail}
          timeline={timeline}
        />
      ) : null}
    </div>
  );
}

function WorkerDetail({
  worker,
  selectedSessionId,
  onSelectSession,
  onViewRoute,
  timelineRequested,
  canReadTrail,
  timeline,
}: {
  worker: workforce.WorkforceWorker;
  selectedSessionId: string | null;
  onSelectSession: (id: string) => void;
  onViewRoute: () => void;
  timelineRequested: boolean;
  /** `mayReadSessionTrail` — BOTH keys the timeline route asks for. */
  canReadTrail: boolean;
  timeline: UseQueryResult<workforce.SessionTimeline>;
}) {
  const selectedSession = worker.sessions.find((s) => s.sessionId === selectedSessionId) ?? null;
  const trailState = sessionTrailState({
    canReadTrail,
    requested: timelineRequested,
    isLoading: timeline.isLoading,
    isError: timeline.isError,
    hasData: timeline.data != null,
  });

  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">{worker.displayName}</h2>
        <span className="rn-muted">{worker.programName ?? 'No program'}</span>
      </div>

      {worker.sessions.length === 0 ? (
        <p className="rn-muted">No sessions recorded on this date.</p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-8)',
          }}
        >
          {worker.sessions.map((s) => (
            <li
              key={s.sessionId}
              data-selected={s.sessionId === selectedSessionId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-10)',
                padding: 'var(--space-8)',
                borderRadius: 'var(--radius-sm)',
                background:
                  s.sessionId === selectedSessionId
                    ? 'var(--color-sky-soft)'
                    : 'var(--color-sunken)',
              }}
            >
              {/* Which session is open was signalled by a background tint and
                  nothing else — invisible to a screen reader. */}
              <button
                type="button"
                className="rn-link"
                aria-current={s.sessionId === selectedSessionId ? 'true' : undefined}
                onClick={() => onSelectSession(s.sessionId)}
              >
                {new Date(s.clockedInAt).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                {' → '}
                {s.clockedOutAt
                  ? new Date(s.clockedOutAt).toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : 'now'}
              </button>
              <span className="rn-muted">
                {s.durationMinutes != null ? formatMinutes(s.durationMinutes) : '—'}
                {s.scheduledMinutes != null
                  ? ` · scheduled ${formatMinutes(s.scheduledMinutes)}`
                  : ''}
              </span>
              <Pill variant={sessionTone(s)} dot>
                {s.openNow ? 'open' : s.status}
              </Pill>
            </li>
          ))}
        </ul>
      )}

      {selectedSession ? (
        <div
          style={{
            marginTop: 'var(--space-16)',
            borderTop: '1px solid var(--color-line)',
            paddingTop: 'var(--space-16)',
          }}
        >
          {trailState === 'refused' ? (
            /*
             * The refusal is PERMANENT, so it is stated once, here, instead of
             * being discovered by pressing a button and reading a retry.
             *
             * Offering the button and letting the server answer is worse than
             * not offering it: it teaches somebody they have an ability they do
             * not, and the 403 came back through `ErrorPanel` — "We couldn't
             * load this route just now", with a Try again — which is the shape
             * of a transient failure. Every retry returns the same 403.
             *
             * `Refused` is the state this screen already uses for the roster
             * beside it when the map itself is withheld, and it names the key
             * that is actually missing: they hold `location_history:view`, or
             * they would not be looking at this screen at all.
             */
            <Refused
              heading="Routes are not shown to you"
              message={`Reading the path a worker’s device actually recorded needs the ${SESSION_TRAIL_KEY} permission. That is separate from seeing where people are on the map, and it is granted to no role by default. The rest of this screen is yours to see; this one part is withheld, and asking your org admin for that permission is what opens it.`}
            />
          ) : trailState === 'prompt' ? (
            <div>
              <button type="button" className="rn-link" onClick={onViewRoute}>
                View route history →
              </button>
            </div>
          ) : trailState === 'loading' ? (
            <SkeletonSet surface="table" rows={4} />
          ) : trailState === 'failed' ? (
            /*
             * Reached only by a reader who holds BOTH keys, so a retry is a
             * real offer rather than a re-refusal.
             */
            <LoadFailure
              error={timeline.error}
              message="We couldn’t load this route just now."
              onRetry={() => void timeline.refetch()}
            />
          ) : timeline.data ? (
            <TimelineDetail timeline={timeline.data} />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function TimelineDetail({ timeline }: { timeline: workforce.SessionTimeline }) {
  const cmp = timeline.scheduledComparison;
  const ratio = timeline.coverage.ratio;
  return (
    <div className="rn-col">
      <div
        style={{ display: 'flex', gap: 'var(--space-10)', alignItems: 'center', flexWrap: 'wrap' }}
      >
        <Pill
          variant={timeline.status === 'open' ? 'good' : timeline.autoClosed ? 'look' : 'plain'}
          dot
        >
          {timeline.status}
        </Pill>
        <span className="rn-muted">
          {/* `SessionTimeline.durationMinutes` is `number | null`, and the
              contract names that null as a withholding with no declaring flag
              (`schedule/service.ts` nulls `computedMinutes` for a caller without
              punch-facts permission). `?? 0` printed "0m" beside the status
              pill — a session that ran eight hours, reported as one that ran
              none, to the reader least entitled to know better. */}
          {timeline.durationMinutes === null
            ? 'Duration not shown'
            : formatMinutes(timeline.durationMinutes)}
          {timeline.autoClosed ? ' · auto-closed, keeping the real times' : ''}
        </span>
        {timeline.shiftLocationName ? (
          <span className="rn-muted">Assigned site: {timeline.shiftLocationName}</span>
        ) : null}
        {cmp ? (
          <span className="rn-muted">
            {formatShiftWindow(cmp.shiftStartsAt, cmp.shiftEndsAt, cmp.shiftTimezone)}
          </span>
        ) : null}
      </div>

      {cmp ? (
        <p style={{ margin: 0 }}>
          Scheduled <strong>{formatMinutes(cmp.scheduledMinutes)}</strong> · actual{' '}
          <strong>{cmp.actualMinutes != null ? formatMinutes(cmp.actualMinutes) : '—'}</strong>
          {cmp.varianceMinutes != null
            ? ` (${cmp.varianceMinutes > 0 ? `${cmp.varianceMinutes} min over` : cmp.varianceMinutes < 0 ? `${-cmp.varianceMinutes} min under` : 'on time'})`
            : ''}
        </p>
      ) : (
        <p className="rn-muted" style={{ margin: 0 }}>
          No scheduled shift — unscheduled session.
        </p>
      )}

      {/* Punches */}
      <div>
        <strong>On the clock</strong>
        <ul
          style={{
            listStyle: 'none',
            margin: 'var(--space-8) 0 0',
            padding: 0,
            display: 'grid',
            gap: 'var(--space-8)',
          }}
        >
          {timeline.punches.map((p) => (
            <li
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-10)',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ minWidth: 64, fontWeight: 600 }}>{p.kind}</span>
              <DualTimestamp occurredAt={p.occurredAt} receivedAt={p.receivedAt} />
              <VerificationBadge
                verification={p.locationVerification as LocationVerification}
                geofenceName={p.siteName ?? undefined}
              />
              {p.latitude != null && p.longitude != null ? (
                <span className="rn-muted">
                  {p.latitude.toFixed(5)}, {p.longitude.toFixed(5)}
                  {p.siteName ? ` · ${p.siteName}` : ''}
                </span>
              ) : (
                <span className="rn-muted">no fix</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Route */}
      <div>
        <strong>Route</strong>
        <p className="rn-muted" style={{ margin: 'var(--space-4) 0 0' }}>
          {timeline.route.pointCount} points · {formatMeters(timeline.route.distanceMeters)}{' '}
          traveled
          {timeline.coverage.status ? ` · coverage ${timeline.coverage.status}` : ''}
          {ratio != null ? ` (${Math.round(ratio * 100)}%)` : ''}
        </p>
        {timeline.route.stops.length > 0 ? (
          <ul style={{ listStyle: 'none', margin: 'var(--space-8) 0 0', padding: 0 }}>
            {timeline.route.stops.map((s) => (
              <li key={s.startedAt} className="rn-muted">
                Stop (inferred): {new Date(s.startedAt).toLocaleTimeString()} →{' '}
                {new Date(s.endedAt).toLocaleTimeString()} ({formatMinutes(s.durationSeconds / 60)})
              </li>
            ))}
          </ul>
        ) : null}
        {timeline.route.gaps.length > 0 ? (
          <ul style={{ listStyle: 'none', margin: 'var(--space-8) 0 0', padding: 0 }}>
            {timeline.route.gaps.map((g) => (
              <li key={`${g.fromIndex}-${g.toIndex}`} className="rn-muted">
                Location gap: {new Date(g.fromCapturedAt).toLocaleTimeString()} →{' '}
                {new Date(g.toCapturedAt).toLocaleTimeString()} ({formatMinutes(g.gapSeconds / 60)})
                — a data-quality signal, not a finding.
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* Breadcrumbs — the raw captured points, as text/coordinates. This is
          the SAME data the map draws as a polyline (nothing fetched
          separately), always rendered here regardless of which map renderer
          is active, so a supervisor never loses the underlying evidence to a
          missing key or a failed script load. */}
      {timeline.breadcrumbs.length > 0 ? (
        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
            Breadcrumbs — {timeline.breadcrumbs.length} point
            {timeline.breadcrumbs.length === 1 ? '' : 's'} recorded
          </summary>
          <ul
            style={{
              listStyle: 'none',
              margin: 'var(--space-8) 0 0',
              padding: 0,
              display: 'grid',
              gap: 'var(--space-6)',
              maxHeight: 260,
              overflowY: 'auto',
            }}
          >
            {timeline.breadcrumbs.map((b) => (
              <li
                key={b.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-10)',
                  flexWrap: 'wrap',
                }}
              >
                <DualTimestamp occurredAt={b.capturedAt} receivedAt={b.receivedAt} />
                <span className="rn-muted">
                  {b.latitude.toFixed(5)}, {b.longitude.toFixed(5)}
                  {b.accuracyMeters != null ? ` · ±${Math.round(b.accuracyMeters)} m` : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {timeline.exceptions.length > 0 ? (
        <div>
          <strong>Worth a look</strong>
          <ul style={{ margin: 'var(--space-8) 0 0', paddingLeft: 'var(--space-16)' }}>
            {timeline.exceptions.map((e) => (
              <li key={e.id}>
                {e.kind} — {e.status} ({e.severity})
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
